import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography, { typographyClasses } from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import DateInput from '~components/FormFields/v2/DateInput';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { MAX_TRUCK_COUNT } from '~pages/Sales/Orders/newOrderFormSchema';

const cloneOrderSchema = yup.object().shape({
  startDate: yup
    .date()
    .typeError(t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
  truckCount: yup.lazy((val) => {
    if (String(val).trim() === '') {
      return yup.string().notRequired();
    }

    const field = t('dispatch.order.delivery_estimates.truck_qty');

    return yup
      .number()
      .nullable()
      .min(1, t('form_validation_errors.min', { field, min: 1 }))
      .max(
        MAX_TRUCK_COUNT,
        t('form_validation_errors.max', { field, max: MAX_TRUCK_COUNT }),
      );
  }),
  copyJobAssignments: yup.boolean().required(),
});
type CloneOrderDTO = yup.InferType<typeof cloneOrderSchema>;

interface CloneOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCloned: (order: Order) => void;
  order?: Order;
}

export default function CloneOrderDialog({
  isOpen,
  onClose,
  onOrderCloned,
  order,
}: CloneOrderDialogProps) {
  const theme = useTheme();
  const defaultTruckCount = order?.orderSummary?.jobsCount ?? 1;

  const form = useForm<CloneOrderDTO>({
    resolver: yupResolver(cloneOrderSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      startDate: dayjs.tz().toDate(),
      truckCount: defaultTruckCount,
      copyJobAssignments: true,
    },
  });

  const orderId = order?.id;
  const { cloneOrderV2 } = useOrdersDispatch();
  const { isSubmitting } = form.formState;

  const [isEditingTruckCount, setIsEditingTruckCount] = useState(false);

  const toggleIsEditingTruckCount = useCallback(() => {
    setIsEditingTruckCount((state) => !state);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = useCallback(() => {
    form.handleSubmit(async (data) => {
      if (!orderId) {
        return;
      }

      try {
        const clonedOrder = await cloneOrderV2({
          orderId,
          date: dayjs.tz(data.startDate).format('YYYY-MM-DD'),
          truckCount:
            isEditingTruckCount && data.truckCount
              ? parseInt(String(data.truckCount), 10)
              : undefined,
          includeAssignee: data.copyJobAssignments,
        });

        if (clonedOrder) {
          onOrderCloned(clonedOrder);
        }
      } catch {
        // Error is surfaced by cloneOrderV2 (toast + Sentry).
      }
    })();
  }, [orderId, onOrderCloned, form, isEditingTruckCount, cloneOrderV2]);

  useEffect(() => {
    if (!isEditingTruckCount) {
      form.setValue('truckCount', defaultTruckCount);
    }
  }, [isEditingTruckCount, defaultTruckCount, form.setValue]);

  return (
    <Dialog isOpen={isOpen} onClose={handleClose}>
      <DialogHeader onClose={handleClose}>{t('dispatch.order.clone_order')}</DialogHeader>

      <DialogContent>
        {/* 250px column width minus the 16px grid spacing */}
        <Grid container spacing={2} sx={{ width: '100%', maxWidth: '234px' }}>
          <Grid size={{ xs: 12 }}>
            <DateInput
              control={form.control}
              dataUi="page-dispatch__clone-order-dialog__start-date-input"
              isRequired
              label={t('order.form.start_date')}
              name="startDate"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isEditingTruckCount ? null : (
                <Typography variant="body2" sx={{ fontSize: '14px' }}>
                  {`${t('dispatch.order.delivery_estimates.truck_qty')}: ${defaultTruckCount}`}
                </Typography>
              )}

              <PseudoLink
                action={toggleIsEditingTruckCount}
                linkColor={theme.brandV2.colors.treadBlue2}
                title={
                  isEditingTruckCount
                    ? `${t('dispatch.order.hide_truck_quantity')}`
                    : `${t('dispatch.order.edit_truck_quantity')}`
                }
                sx={{ [`& .${typographyClasses.root}`]: { fontSize: '14px' } }}
              />
            </Box>

            {isEditingTruckCount && (
              <NumberInput
                control={form.control}
                dataUi="page-dispatch__clone-order-dialog__truck-count-input"
                label={t('dispatch.order.delivery_estimates.truck_qty')}
                name="truckCount"
                sx={{ mt: 2 }}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CheckboxInput
              control={form.control}
              label={t('form_fields.copy_job_assignments')}
              name="copyJobAssignments"
              sx={{
                [`& .${typographyClasses.root}`]: { fontSize: '12px', fontWeight: 400 },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton loading={isSubmitting} onClick={handleSubmit}>
          {t('actions.clone')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
